import { DndContext, MouseSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { TableHeader, TableRow, TableHead, cn } from 'twyne-new.ui'

import ColumnHeaderMenu, { SortToggle } from '@Components/ColumnsConfig/HeaderMenu'
import { isAlwaysPinned, isColumnConfigurable } from '@Components/ColumnsConfig/config'
import { useTableResizeContext } from '@Components/tableResize/context'
import { useColumnWidth } from '@Components/tableResize/hooks'
import { usePinnedColumns, usePinnedHeaderCell } from '@Components/tableResize/pinned'

import ColumnResizer from './ColumnResizer'

export const Header = ({ columns, sticky, onResize, onTogglePinColumn, onRemoveColumn, onReorderColumns, onSortColumn, sortState, onOpenColumnsConfig }) => {
  const { headerRowRef } = usePinnedColumns()
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }))
  const ids = columns.map(columnId)

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return
    }

    onReorderColumns?.(active.id, over.id)
  }

  return <TableHeader className='z-30 border-b twy-border-base' sticky={sticky}>
    <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToHorizontalAxis]} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
        <TableRow ref={headerRowRef}>
          {columns.map((column, index) => <HeaderItem
            key={columnId(column, index)}
            column={column}
            index={index}
            draggable={isDraggable(column, onReorderColumns)}
            onResize={onResize}
            onTogglePinColumn={onTogglePinColumn}
            onRemoveColumn={onRemoveColumn}
            onSortColumn={onSortColumn}
            sortState={sortState}
            onOpenColumnsConfig={onOpenColumnsConfig}
          />)}
        </TableRow>
      </SortableContext>
    </DndContext>
  </TableHeader>
}

function columnId(column, index) {
  return column.dataField ?? `column-${index}`
}

function isDraggable(column, onReorderColumns) {
  return Boolean(onReorderColumns) && column.enableColumnConfig !== false && column.allowReorder !== false
}

const HeaderItem = ({ column, index, draggable, onResize, onTogglePinColumn, onRemoveColumn, onSortColumn, sortState, onOpenColumnsConfig }) => {
  const { activeResizeColumn, resizedColumns } = useTableResizeContext()
  const columnWidth = useColumnWidth({ columnIndex: index })
  const canBreakHeaderText = activeResizeColumn === index || resizedColumns.includes(index)
  const pinnedCell = usePinnedHeaderCell(index)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: columnId(column, index), disabled: !draggable })
  const configurable = isColumnConfigurable(column) && !isAlwaysPinned(column)
  const canSort = Boolean(column.customSort) && Boolean(onSortColumn)
  const direction = sortState && column.dataField && sortState.dataField === column.dataField ? sortState.direction : null

  return <TableHead
    ref={setNodeRef}
    {...attributes}
    {...listeners}
    className={cn(
      'relative group capitalize whitespace-nowrap',
      canBreakHeaderText && 'whitespace-normal break-all',
      column.headerClassName,
      pinnedCell.className,
      draggable && 'cursor-grab',
      isDragging && 'cursor-grabbing z-40 opacity-80',
    )}
    style={{ ...columnWidth, ...pinnedCell.style, transform: CSS.Translate.toString(transform), transition }}
  >
    <ColumnResizer
      canResize={column.enableResize}
      isResizing={activeResizeColumn === index}
      onMouseDown={event => onResize({ columnIndex: index, event })}
    />
    <div className='flex items-center gap-1'>
      {headerText(column, index)}
      {canSort && <SortToggle direction={direction} onClick={() => onSortColumn(column, direction ? 'desc' : 'asc')} />}
      <ColumnHeaderMenu
        pinned={pinnedCell.pinned}
        sortDirection={direction}
        onSortAsc={canSort ? () => onSortColumn(column, 'asc') : undefined}
        onSortDesc={canSort ? () => onSortColumn(column, 'desc') : undefined}
        onTogglePin={configurable && onTogglePinColumn ? () => onTogglePinColumn(column) : undefined}
        onHide={configurable && onRemoveColumn ? () => onRemoveColumn(column) : undefined}
        onOpenAllColumns={onOpenColumnsConfig}
      />
    </div>
  </TableHead>
}

function headerText({ text }, index) {
  if (!text) {
    return ''
  }

  return typeof text === 'function' ? text('', {}, index) : text
}
